import { Injectable } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { ApexAxisChartSeries } from 'ng-apexcharts';
import {
  GapSlider,
  GapSliderInterface,
} from '../../../interfaces/gap-slider.interface';
import { ApkService } from '../../pages/apk/apk.service';
import { positiveIntOnly } from '../../pages/apk/apk/validators';
import {
  DepreciationItem,
  depreciationTable,
} from '../../pages/apk/models/depreciation-table.class';

@Injectable({
  providedIn: 'root',
})
export class DepreciationChartService {
  private depreciationTable: DepreciationItem[] = depreciationTable;
  private _gapSliderFormGroup: UntypedFormGroup;
  private _gapSliderDto: GapSliderInterface;

  get gapSliderFormGroup(): UntypedFormGroup {
    return this._gapSliderFormGroup;
  }

  set gapSliderFormGroup(value: UntypedFormGroup) {
    this._gapSliderFormGroup = value;
  }

  get gapSliderDto(): GapSliderInterface {
    return this._gapSliderDto;
  }

  set gapSliderDto(value: GapSliderInterface) {
    this._gapSliderDto = value;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private apkService: ApkService,
  ) {}

  public getSeries(
    vehicleInsuredSum: number,
    insurancePeriodMax: number,
  ): {
    chartSeries: ApexAxisChartSeries;
    chartSeriesCategories: string[];
    chartBarSeries: ApexAxisChartSeries;
  } {
    const response = {
      chartSeries: [
        {
          name: 'Wartość pojazdu',
          data: [vehicleInsuredSum],
        },
      ],
      chartSeriesCategories: ['Dziś'],
      chartBarSeries: [
        {
          name: 'offenderInsurance',
          data: [],
        },
        {
          name: 'gapInsurance',
          data: [],
        },
        {
          name: 'insuranceLoss',
          data: [],
        },
      ],
    };

    const insurancePeriodYears = insurancePeriodMax / 12;

    for (let i = 1; i <= insurancePeriodYears; i++) {
      const priceDecreaseForPeriod = this.depreciationTable.find(
        (item) => item.month === i * 12,
      );
      response.chartSeries[0].data[i] = Math.round(
        vehicleInsuredSum -
          vehicleInsuredSum * priceDecreaseForPeriod.priceDecrease,
      );
      response.chartSeriesCategories.push(i + ` ${this.getPluralRules(i)}`);
    }

    return response;
  }

  getPluralRules(insurancePeriod: number) {
    const suffixes = new Map([
      ['1', 'rok'],
      ['2', 'lata'],
      ['3', 'lata'],
      ['4', 'lata'],
      ['5', 'lat'],
      ['6', 'lat'],
    ]);
    const formatOrdinals = (n: number) => {
      const suffix = suffixes.get(n.toString());
      return `${suffix}`;
    };

    return formatOrdinals(insurancePeriod);
  }

  initializeSliderForm(): void {
    this.gapSliderDto = new GapSlider();
    this.gapSliderFormGroup = this.fb.group({
      vehiclePurchasePrice: [
        this.gapSliderDto.vehiclePurchasePrice,
        [positiveIntOnly(), RxwebValidators.maxNumber({ value: 600000 })],
      ],
      monthOfUse: [this.gapSliderDto.monthOfUse],
      monthsArray: [this.gapSliderDto.monthsArray],
      policyValue: [this.gapSliderDto.policyValue, [Validators.required]],
      gapValue: [this.gapSliderDto.gapValue, [Validators.required]],
    });
    this.gapSliderFormGroup.controls.monthOfUse.patchValue(36);
    this.gapSliderFormGroup.controls.monthsArray.patchValue([
      60 - this.gapSliderFormGroup.controls.monthOfUse.value,
      59,
    ]);
  }

  handleSliding(): void {
    this.gapSliderFormGroup.controls.monthOfUse.patchValue(
      60 - this.gapSliderFormGroup.controls.monthsArray.value[0],
    );
    if (this.gapSliderFormGroup.controls.vehiclePurchasePrice.value) {
      this.gapSliderFormGroup.controls.policyValue.patchValue(
        this.apkService.getVehiclePriceHomePage(
          this.gapSliderFormGroup.controls.monthOfUse.value,
          this.gapSliderFormGroup.controls.vehiclePurchasePrice.value,
        ),
      );
      this.calculateGapValue();
    }
  }

  calculateGapValue(): void {
    if (
      this.gapSliderFormGroup.controls.vehiclePurchasePrice.value -
        this.gapSliderFormGroup.controls.policyValue.value <
      150000
    ) {
      this.gapSliderFormGroup.controls.gapValue.patchValue(
        this.gapSliderFormGroup.controls.vehiclePurchasePrice.value -
          this.gapSliderFormGroup.controls.policyValue.value,
      );
    } else {
      this.gapSliderFormGroup.controls.gapValue.patchValue(150000);
    }
  }
}
